import { Injectable } from '@angular/core';
import { Platform, ToastController } from '@ionic/angular';
import { OneSignal } from '@ionic-native/onesignal/ngx';
import { BehaviorSubject, Observable, fromEvent, merge } from 'rxjs';
import { take } from 'rxjs/operators';
import { ResourceNavigationService } from './resourcenavigation.service';
import { ResourceType } from '../../models/resourcetype';

export type PushPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

// Sent as the OneSignal notification's `data` payload (see backend's OneSignalPushSender) - some
// push transports (FCM/APNs) only carry string values, so fields are read defensively, not assumed
// to still be a number, when a notification is tapped.
interface PushResourceData {
	resourceType?: number | string;
	resourceId?: string;
}

interface OneSignalWebNotificationClickEvent {
	notification: { additionalData?: PushResourceData };
}

interface OneSignalWebForegroundWillDisplayEvent {
	preventDefault(): void;
	notification: { title: string; body: string; additionalData?: PushResourceData };
}

interface OneSignalWebNotifications {
	requestPermission(): Promise<boolean>;
	addEventListener(event: 'permissionChange', listener: (permission: NotificationPermission) => void): void;
	addEventListener(event: 'click', listener: (event: OneSignalWebNotificationClickEvent) => void): void;
	addEventListener(event: 'foregroundWillDisplay', listener: (event: OneSignalWebForegroundWillDisplayEvent) => void): void;
}

interface OneSignalWeb {
	init(options: { appId: string; serviceWorkerPath?: string; serviceWorkerParam?: { scope: string } }): Promise<void>;
	login(externalId: string): Promise<void>;
	Notifications: OneSignalWebNotifications;
	User: {
		PushSubscription: {
			addEventListener(event: 'change', listener: (e: { current: { id: string; token: string; optedIn: boolean } }) => void): void;
		};
	};
}

declare global {
	interface Window {
		OneSignalDeferred?: Array<(oneSignal: OneSignalWeb) => void>;
	}
}

// Web push only works inside an installed, standalone home-screen PWA on iOS Safari - a regular
// browser tab can't get permission there at all, so this is checked up front instead of attempting
// a doomed prompt.
function isWebPushSupported(): boolean {
	if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
		return false;
	}
	const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
	return !isIos || (window.navigator as { standalone?: boolean }).standalone === true;
}

@Injectable()

export class PushService {

	// Same app across native and web platforms - the backend only holds one configured OneSignal
	// App ID (OneSignalOptions.AppId) to send with, so both have to register under this one.
	private readonly oneSignalAppId = '740c0dbe-b37d-4b5e-be3b-f9e1e8946c9c';

	private currentIds: {
		userId: string;
		pushToken: string;
	};

	private readonly permissionStateSubject: BehaviorSubject<PushPermissionState>;
	permissionState$: Observable<PushPermissionState>;

	constructor(private oneSignal: OneSignal, private platform: Platform, private resourceNavigationService: ResourceNavigationService) {
		this.permissionStateSubject = new BehaviorSubject<PushPermissionState>(this.readPermissionState());
		this.permissionState$ = this.permissionStateSubject.asObservable();
	}

	// shouldPrompt=false skips the actual OS permission prompt (SDK init/listeners still happen, so a
	// later requestPermission() call - e.g. from the settings page or the permissions onboarding modal
	// - still works) - used to hold off prompting until the user has seen the onboarding explainer.
	register(toastCtrl: ToastController, shouldPrompt: boolean = true): Promise<{}> {
		if (this.platform.is('cordova')) {
			return new Promise((resolve, reject) => {

				this.oneSignal.startInit(this.oneSignalAppId, '377337889917');
				this.oneSignal.inFocusDisplaying(this.oneSignal.OSInFocusDisplayOption.None);
				this.oneSignal.iOSSettings({ kOSSettingsKeyAutoPrompt: false, kOSSettingsKeyInAppLaunchURL: false});
				if (shouldPrompt) {
					this.oneSignal.promptForPushNotificationsWithUserResponse().then(success => {
						console.log('Enabled push notifications');
					}).then(error => {
						console.log('Disabled push notifications');
					});
				}
				// OSInFocusDisplayOption.None above already suppresses the native in-focus alert - this is
				// what shows the user something instead while the app is open.
				this.oneSignal.handleNotificationReceived().subscribe((notification) => {
					this.presentForegroundToast(toastCtrl, notification.payload.title, notification.payload.body, notification.payload.additionalData);
				});
				this.oneSignal.handleNotificationOpened().subscribe((result) => {
					this.navigateFromPushData(result.notification.payload?.additionalData);
				});
				this.oneSignal.endInit();

				this.oneSignal.getIds().then(ids => {
					console.debug('[Push] data retrieved', ids);
					this.currentIds = ids;
					resolve(ids);
				}).catch(error => {
					console.debug('[Push][Error] not retrieved', error);
					reject(error);
				});
			});
		}

		if (!isWebPushSupported()) {
			return Promise.resolve({});
		}

		return this.withWebOneSignal(async (oneSignal) => {
			// Registered under its own scope, not '/', so it doesn't fight ngsw-worker.js (registered
			// at root in app.module.ts) for control of the page - both need to be active at once.
			await oneSignal.init({
				appId: this.oneSignalAppId,
				serviceWorkerPath: 'push/onesignal/OneSignalSDKWorker.js',
				serviceWorkerParam: { scope: '/push/onesignal/' }
			});
			oneSignal.Notifications.addEventListener('permissionChange', () => {
				this.permissionStateSubject.next(this.readPermissionState());
			});
			oneSignal.Notifications.addEventListener('click', (event) => {
				this.navigateFromPushData(event.notification.additionalData);
			});
			// Stops the browser's native notification from appearing while the app is open in a tab -
			// same intent as OSInFocusDisplayOption.None on native - and shows our own toast instead.
			oneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
				event.preventDefault();
				this.presentForegroundToast(toastCtrl, event.notification.title, event.notification.body, event.notification.additionalData);
			});
			oneSignal.User.PushSubscription.addEventListener('change', (e) => {
				console.debug('[Push] subscription changed', e.current);
			});
			// WebKit silently no-ops Notification.requestPermission() when it's not called inside a
			// real user gesture (unlike Chromium, which allows it outside one) - so on an installed
			// iOS PWA, prompting immediately here at boot would never actually show a dialog. Defer to
			// the user's first tap, same pattern app.component.ts uses for geolocation on web.
			// Calls oneSignal.Notifications.requestPermission() directly on the instance already
			// resolved above, instead of via requestPermission() (which re-queues onto
			// OneSignalDeferred) - that extra hop breaks the synchronous gesture chain WebKit
			// requires, silently swallowing the prompt.
			if (shouldPrompt) {
				merge(fromEvent(document, 'click'), fromEvent(document, 'touchstart')).pipe(take(1)).subscribe(async () => {
					await oneSignal.Notifications.requestPermission();
					this.permissionStateSubject.next(this.readPermissionState());
				});
			}
			return {};
		}, {});
	}

	// Lets the backend target this user via OneSignal's include_external_user_ids using our own
	// User.Id, instead of us tracking/storing OneSignal's own player id ourselves.
	setExternalUserId(userId: string): void {
		if (this.platform.is('cordova')) {
			this.oneSignal.setExternalUserId(userId);
		} else if (isWebPushSupported()) {
			this.withWebOneSignal(oneSignal => oneSignal.login(userId), undefined);
		}
	}

	// Re-prompts for browser notification permission - only actually shows a dialog while permission
	// is still 'default'; browsers refuse to re-open it once the user has explicitly denied it, so
	// this just returns the current (already-decided) state in that case.
	requestPermission(): Promise<PushPermissionState> {
		if (this.platform.is('cordova') || !isWebPushSupported()) {
			return Promise.resolve(this.readPermissionState());
		}

		return this.withWebOneSignal(async (oneSignal) => {
			await oneSignal.Notifications.requestPermission();
			const state = this.readPermissionState();
			this.permissionStateSubject.next(state);
			return state;
		}, this.readPermissionState());
	}

	private presentForegroundToast(toastCtrl: ToastController, title: string, body: string, data: PushResourceData | undefined): void {
		const resourceType = Number(data?.resourceType);
		const hasResource = !isNaN(resourceType) && !!data?.resourceId;

		toastCtrl.create({
			header: title,
			message: body,
			duration: 4000,
			position: 'top',
			buttons: hasResource ? [{ text: 'Bekijk', handler: () => this.navigateFromPushData(data) }] : undefined
		}).then(toast => toast.present());
	}

	private navigateFromPushData(data: PushResourceData | undefined): void {
		const resourceType = Number(data?.resourceType);
		if (isNaN(resourceType) || !data?.resourceId) {
			return;
		}
		this.resourceNavigationService.navigate(resourceType as ResourceType, data.resourceId);
	}

	private readPermissionState(): PushPermissionState {
		if (this.platform.is('cordova')) {
			return 'granted';
		}
		return isWebPushSupported() ? Notification.permission : 'unsupported';
	}

	// Queues a callback on OneSignal's own load queue (window.OneSignalDeferred) - the documented
	// integration pattern, works whether the CDN script has finished loading yet or not. Races
	// against a timeout because ad-blockers commonly block onesignal.com's script, which would
	// otherwise leave the queued callback - and this promise - unresolved forever.
	private withWebOneSignal<T>(fn: (oneSignal: OneSignalWeb) => T | Promise<T>, fallback: T): Promise<T> {
		return Promise.race([
			new Promise<T>((resolve) => {
				window.OneSignalDeferred = window.OneSignalDeferred || [];
				window.OneSignalDeferred.push(async (oneSignal) => {
					try {
						resolve(await fn(oneSignal));
					} catch {
						resolve(fallback);
					}
				});
			}),
			new Promise<T>((resolve) => setTimeout(() => resolve(fallback), 5000))
		]);
	}
}
